import Lottie from 'lottie-react';
import styled, { css, keyframes } from 'styled-components';

const LOTTIE_SIZE = 200;

export const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  padding-bottom: 80px;
  gap: 16px;
  overflow: hidden;
`;

export const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black1};
`;

export const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
`;

const wave = keyframes`
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(1.5);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
`;

const glowBreathing = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.7;
  }
`;

export type AvatarStatus = 'speaking' | 'listening' | 'preparing' | 'error' | 'idle';

const getGlowColor = (status: AvatarStatus) => {
  switch (status) {
    case 'speaking':
      return 'rgba(82, 196, 26, 0.8)';
    case 'listening':
      return 'rgba(82, 196, 26, 0.5)';
    case 'preparing':
      return 'rgba(250, 173, 20, 0.6)';
    case 'error':
      return 'rgba(255, 77, 79, 0.6)';
    default:
      return 'rgba(82, 196, 26, 0.3)';
  }
};

const getGlowAnimation = (status: AvatarStatus) => {
  switch (status) {
    case 'speaking':
      return css`
        animation: ${glowPulse} 1.2s ease-in-out infinite;
      `;
    case 'listening':
      return css`
        animation: ${glowBreathing} 2.5s ease-in-out infinite;
      `;
    case 'preparing':
      return css`
        animation: ${glowBreathing} 1.8s ease-in-out infinite;
      `;
    default:
      return '';
  }
};

export const AvatarContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AvatarGlow = styled.div<{ $status: AvatarStatus }>`
  position: absolute;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    ${({ $status }) => getGlowColor($status)} 0%,
    transparent 70%
  );
  filter: blur(8px);
  ${({ $status }) => getGlowAnimation($status)}
  transition: background 0.4s ease;
`;

export const AvatarWrapper = styled.div<{ $status?: AvatarStatus }>`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.green6} 0%,
    ${({ theme }) => theme.colors.green5} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1),
    0 0 30px ${({ $status }) => getGlowColor($status || 'idle')};
  transition: box-shadow 0.4s ease;
  z-index: 1;

  ${({ $status }) =>
    $status === 'speaking' &&
    css`
      animation: ${pulse} 1.5s ease-in-out infinite;
    `}

  img {
    width: 80px;
    height: 80px;
    object-fit: contain;
  }
`;

export const AudioWaves = styled.div<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 8px 16px;
  border-radius: 20px;
  background: ${({ theme, $isActive }) => ($isActive ? `${theme.colors.green6}10` : 'transparent')};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.4)};
  transition: all 0.3s ease;
`;

export const Wave = styled.div<{ $delay: number; $isActive?: boolean; $height?: number }>`
  width: 4px;
  height: ${({ $height }) => $height || 16}px;
  background-color: ${({ theme }) => theme.colors.green6};
  border-radius: 2px;
  transition: height 0.1s ease;
  animation: ${({ $isActive }) =>
    $isActive
      ? css`
          ${wave} 0.6s ease-in-out infinite
        `
      : 'none'};
  animation-delay: ${({ $delay }) => $delay}ms;
`;

export const StatusText = styled.p<{ $status?: string }>`
  margin: 0;
  font-size: 16px;
  color: ${({ theme, $status }) => {
    switch ($status) {
      case 'CONNECTED':
      case 'ACTIVE':
        return theme.colors.green6;
      case 'CONNECTING':
      case 'PREPARING':
        return theme.colors.royal;
      case 'ERROR':
        return theme.colors.red6;
      default:
        return theme.colors.black2;
    }
  }};
  font-weight: 500;
  transition: color 0.3s ease;
`;

export const ControlsWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

export const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
  gap: 16px;
`;

export const EmptyStateTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black1};
`;

export const EmptyStateDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black3};
  max-width: 280px;
`;

export const StartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 28px;
  border: none;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.green6} 0%,
    ${({ theme }) => theme.colors.green5} 100%
  );
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(82, 196, 26, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(82, 196, 26, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const ErrorMessage = styled.div`
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.colors.red1};
  border: 1px solid ${({ theme }) => theme.colors.red3};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.red6};
  font-size: 14px;
  text-align: center;
`;

export const StyledLottie = styled(Lottie)`
  width: 200px;
  height: 200px;
  align-self: center;
`;

export const LoadingWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 128px 24px 0px 16px;
`;

export const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${LOTTIE_SIZE}px;
  height: ${LOTTIE_SIZE}px;
`;

export const AnimatedLottie = styled(Lottie)`
  width: 100%;
  height: 100%;
`;

export const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black3};
  text-align: center;
`;
